import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, UserPlus, Chrome, Gamepad2, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { LogoMark } from "../components/Logo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const inputCls = "mt-2 w-full rounded-xl border border-line bg-ink px-4 py-3 text-sm text-white outline-none transition-colors focus:border-signal";

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refCode, setRefCode] = useState(() => localStorage.getItem("ftc_ref") || "");
  const [next, setNext] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      const clean = ref.trim().toLowerCase();
      localStorage.setItem("ftc_ref", clean);
      setRefCode(clean);
      setMode("signup");
    }
    const nxt = params.get("next");
    if (nxt && nxt.startsWith("/")) {
      setNext(nxt);
      localStorage.setItem("ftc_next", nxt);
      setMode("signup");
    }
  }, []);

  useEffect(() => {
    if (user) navigate(next || "/", { replace: true });
  }, [user, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "signin") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password, refCode);
      }
      toast.success(mode === "signin" ? "WELCOME BACK TO THE CLUB" : "MEMBERSHIP ACTIVATED");
      localStorage.removeItem("ftc_next");
      navigate(next || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="auth-page">
      <Header />
      <div className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-5 py-16">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2 rounded-full bg-signal/15 blur-[140px]" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="card-violet relative w-full max-w-md p-8"
        >
          <div className="flex justify-center"><LogoMark size={44} /></div>
          <h1 className="mt-5 text-center font-display text-2xl font-800 tracking-tight text-white">
            {mode === "signin" ? "Welcome back, trader" : "Join Funded Traders Club"}
          </h1>
          <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
            {mode === "signin" ? "Sign in to review firms & log trades" : "Free membership — reviews, journal, tools"}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2 rounded-2xl border border-line bg-ink p-1.5" data-testid="auth-mode-toggle">
            {[
              { key: "signin", label: "Sign In" },
              { key: "signup", label: "Sign Up" },
            ].map((m) => (
              <button key={m.key} onClick={() => { setMode(m.key); setError(""); }} data-testid={`auth-mode-${m.key}`}
                className={`rounded-xl px-4 py-2.5 font-display text-sm font-700 transition-all duration-200 ${
                  mode === m.key ? "bg-signal text-white" : "text-muted hover:text-white"
                }`}>
                {m.label}
              </button>
            ))}
          </div>

          {mode === "signup" && refCode && (
            <p className="mt-4 flex items-center gap-2 rounded-xl border border-gold/40 bg-gold/10 px-4 py-2.5 font-mono text-[11px] text-gold" data-testid="referral-note">
              <Gift size={13} /> Invited with code {refCode} — you both earn 0.15 FTC coin.
            </p>
          )}

          {next === "/wallet" && (
            <p className="mt-4 flex items-center gap-2 rounded-xl border border-signal/40 bg-signal/10 px-4 py-2.5 font-mono text-[11px] text-signal" data-testid="wallet-gate-note">
              <Gift size={13} /> Create a free account to open your FTC Wallet.
            </p>
          )}

          {error && (
            <p className="mt-5 rounded-xl border border-loss/50 bg-loss/10 px-4 py-2.5 font-mono text-[11px] text-loss" data-testid="auth-error">
              {error}
            </p>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4">
            {mode === "signup" && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Trader Name</label>
                <input required minLength={2} maxLength={60} data-testid="auth-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} placeholder="How the club knows you" />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Email</label>
              <input type="email" required autoComplete="email" data-testid="auth-email-input"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls} placeholder="you@email.com" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Password</label>
              <input type="password" required minLength={6} autoComplete={mode === "signin" ? "current-password" : "new-password"} data-testid="auth-password-input"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={inputCls} placeholder="••••••••" />
            </div>
            <button type="submit" disabled={busy} data-testid="auth-submit-btn"
              className="btn-beam btn-gradient flex w-full items-center justify-center gap-2 px-5 py-3.5 font-display text-sm font-800 text-white transition-opacity disabled:opacity-50">
              {mode === "signin" ? <LogIn size={14} /> : <UserPlus size={14} />}
              {busy ? "Working…" : mode === "signin" ? "Sign In" : "Create Free Account"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-line" />
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-muted">Or continue with</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          <button onClick={googleLogin} data-testid="auth-google-btn"
            className="flex w-full items-center justify-center gap-2.5 rounded-full bg-white px-5 py-3.5 font-display text-sm font-800 text-gray-900 transition-all duration-200 hover:shadow-[0_0_28px_rgba(255,255,255,0.2)]">
            <Chrome size={16} /> Continue with Google
          </button>
          <button disabled data-testid="auth-discord-btn" title="Discord login needs app credentials — coming soon"
            className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-2.5 rounded-full bg-[#5865F2]/40 px-5 py-3.5 font-display text-sm font-800 text-white/60">
            <Gamepad2 size={16} /> Discord — Coming Soon
          </button>
          <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-[0.15em] text-muted">
            Member reviews are linked to real profiles — no anonymous noise.
          </p>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
